import { Alert, Center, Loader, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons';
import { MappingsTab } from '../../../components/integrations/components/MappingsTab';
import { getIntegrationMeta as getIntegrationBrand } from '../../../components/integration-meta';
import { useIntegrationObjects } from '../../../queries/use-integration-objects';
import { useIntegrationObjectsMeta } from '../../../queries/use-integration-objects-meta';
import type { MappingsData } from '../../../types/integration-meta';

interface ObjectMappingsPanelProps {
  accountId: string;
  integrationKey: string;
}

/**
 * Native scaffolding that feeds the lifted `MappingsTab` an admin-native `MappingsData` prop
 * (no dashboard `{ available, connected }` shim / `as unknown as` casts).
 *
 * - `externalObjects`: `GET /admin/integration-objects` (configured objects). The endpoint joins
 *   each one's `fieldsMeta` from `integration_objects_meta` server-side, for the page it returns -
 *   the source-field, date-field and id-field pickers read `externalObj.fieldsMeta`, and that value
 *   comes only from the catalogue, never from whatever the configured document happens to carry.
 * - `availableObjects`: `GET /admin/integration-objects-meta` — the origin-object catalog, which
 *   un-gates "Add object". An empty page means the integration's metadata hasn't been synced yet
 *   (a customer-side refresh flow, not an admin action) — so we render the configured objects and
 *   show a hint instead of erroring.
 * - Edits/adds save via `PUT /admin/integration-objects`; delete stays gated (no admin DELETE) —
 *   use the per-row Disable toggle.
 */
export function ObjectMappingsPanel({ accountId, integrationKey }: ObjectMappingsPanelProps) {
  const { data, isLoading, isError } = useIntegrationObjects({ accountId, origin: integrationKey });
  const metaQuery = useIntegrationObjectsMeta(accountId, integrationKey);

  const brand = getIntegrationBrand(integrationKey);
  const availableObjects = metaQuery.data?.items ?? [];
  const metaNotSynced = metaQuery.isSuccess && availableObjects.length === 0;

  const mappingsData: MappingsData | undefined = data
    ? {
        integrationKey,
        integrationName: brand.label,
        mustSelectDateField: false,
        externalObjects: data.items,
        availableObjects,
      }
    : undefined;

  return mappingsData ? (
    <Stack spacing="md">
      {metaNotSynced ? (
        <Alert icon={<IconInfoCircle size={16} />} color="yellow" variant="light">
          Integration metadata hasn&apos;t been synced yet for this account, so the source-object
          catalog is unavailable. You can edit configured objects, but &ldquo;Add object&rdquo; and
          the field pickers stay empty until metadata is refreshed (a customer-side flow).
        </Alert>
      ) : null}
      <MappingsTab data={mappingsData} isUpdatingMapping={false} />
    </Stack>
  ) : isLoading ? (
    <Center mih={200}>
      <Loader />
    </Center>
  ) : isError ? (
    <Text color="red">Failed to load integration objects.</Text>
  ) : (
    <Text color="dimmed">No objects found.</Text>
  );
}
